import { keyframes } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

import { ToastPlacement } from './Toast.props';
import { ToastVariant } from '../../../utils/toast';

/**
 * Fallback palette for the contexts that build a partial `v4` theme — the same
 * defensive read `TablePill` uses. Mirrors `lightTheme.palette.v4.toast`.
 */
export const TOAST_TONES = {
  surface: '#0f172a',
  surfaceRecessed: '#475569',
  border: '#1e293b',
  title: '#f8fafc',
  body: '#94a3b8',
  success: '#34d399',
  error: '#f87171',
  warning: '#fbbf24',
  info: '#60a5fa',
  track: 'rgba(148, 163, 184, 0.24)',
  hoverOverlay: 'rgba(255, 255, 255, 0.08)',
  shadow: '0 10px 30px rgba(15, 23, 42, 0.28)',
};

/**
 * The card's type scale. Deliberately its own, not a `Typography` variant: the
 * variants are tuned for the light page, and the title has to hold its weight
 * against a dark surface at a size the rest of the app never uses.
 */
export const TOAST_TYPE = {
  title: { fontSize: '14px', fontWeight: 600 },
  body: { fontSize: '13px' },
  action: { fontSize: '14px', fontWeight: 600 },
} as const;

/** The house easing, shared with BottomSheet — quick start, soft landing. */
export const TOAST_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
export const TOAST_ENTER_MS = 220;

/**
 * How far a receding card peeks out from behind the one in front of it. Sized
 * to clear the card's top padding and its title line — a glimpse that shows
 * nothing legible is just a shadow.
 */
export const STACK_PEEK_PX = 34;
/** Gap between cards once the stack is fanned out. */
export const STACK_GAP_PX = 10;

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

export const toastTones = (theme: Theme): typeof TOAST_TONES =>
  theme.palette.v4?.toast ?? TOAST_TONES;

export const toastAccent = (theme: Theme, variant: ToastVariant): string =>
  toastTones(theme)[variant];

/** The countdown: a filled bar draining left to right over the toast's lifetime. */
const deplete = keyframes`
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
`;

export interface ToastCardGeometry {
  depth: number;
  offset: number;
  placement: ToastPlacement;
  expanded: boolean;
  isExiting: boolean;
  /** False for the frame before the card's first paint, so it animates in. */
  isSettled: boolean;
}

export const toastCardSx = (
  variant: ToastVariant,
  { depth, offset, placement, expanded, isExiting, isSettled }: ToastCardGeometry
): SxProps<Theme> => {
  // The stack grows away from its anchored edge: bottom-anchored cards travel
  // up, top-anchored cards travel down.
  const direction = placement === 'bottom' ? -1 : 1;
  const scale = expanded || depth === 0 ? 1 : Math.max(0.9, 1 - depth * 0.04);
  const opacity = expanded || depth === 0 ? 1 : Math.max(0.4, 1 - depth * 0.28);

  // Enter and exit are the same gesture in opposite directions: in from the
  // right on desktop, down from the top edge on mobile.
  const restingY = direction * offset;
  const enterX = placement === 'bottom' ? 24 : 0;
  const enterY = placement === 'bottom' ? restingY : restingY - 16;
  const isOffstage = isExiting || !isSettled;

  return (theme) => ({
    position: 'absolute',
    [placement]: 0,
    left: 0,
    right: 0,
    pointerEvents: isExiting ? 'none' : 'auto',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: '10px',
    border: '1px solid',
    borderColor: toastTones(theme).border,
    // A receding card is glimpsed, never read. Flattening it to one tone stops
    // three stacked cards reading as three competing messages.
    backgroundColor:
      expanded || depth === 0 ? toastTones(theme).surface : toastTones(theme).surfaceRecessed,
    boxShadow: toastTones(theme).shadow,
    transformOrigin: placement === 'bottom' ? 'bottom center' : 'top center',
    transform: isOffstage
      ? `translate(${enterX}px, ${enterY}px) scale(${scale})`
      : `translate(0px, ${restingY}px) scale(${scale})`,
    opacity: isOffstage ? 0 : opacity,
    zIndex: 100 - depth,
    transition: [
      `transform ${TOAST_ENTER_MS}ms ${TOAST_EASING}`,
      `opacity ${TOAST_ENTER_MS}ms ${TOAST_EASING}`,
      `background-color ${TOAST_ENTER_MS}ms ${TOAST_EASING}`,
    ].join(', '),
    [REDUCED_MOTION]: {
      // No travel and no scaling; the card simply fades in at its resting spot.
      transform: `translate(0px, ${restingY}px)`,
      transition: `opacity ${TOAST_ENTER_MS}ms linear`,
    },
  });
};

/**
 * The drain. Mounted with a `key` on the collapse count, so a repeat toast
 * folding into this one visibly restarts the countdown.
 */
export const toastProgressSx =
  (
    variant: ToastVariant,
    { duration, paused }: { duration: number; paused: boolean }
  ): SxProps<Theme> =>
  (theme) => ({
    height: '3px',
    flexShrink: 0,
    backgroundColor: toastTones(theme).track,
    '&::after': {
      content: '""',
      display: 'block',
      height: '100%',
      transformOrigin: 'left center',
      backgroundColor: toastAccent(theme, variant),
      animation: `${deplete} ${duration}ms linear forwards`,
      animationPlayState: paused ? 'paused' : 'running',
      // A static full bar, not `animation: none` — that would leave it drained
      // to zero and read as a broken control.
      [REDUCED_MOTION]: {
        animation: 'none',
        transform: 'scaleX(1)',
      },
    },
  });
